import { type Event as DeviceEvent } from '@eppendorf/vnls-notifications-utils';
import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getFormattedLocalizedDateWithMonthName } from '$shared/utils/date.utils';

import { Card } from '$components/card';
import { DeviceNameCell } from '$components/device/name-cell/name-cell';
import { formatRunDuration, isKnownUser } from '$features/report-hub/report-hub.utils';
import {
  type RunRecordReportDetailModel,
  type RunRecordStep,
} from '$features/report-hub/run-record/run-record-detail.types';
import { getRunRecordOutcomeAppearance } from '$features/report-hub/run-record/run-record-outcome';
import { EventCountsRow } from '$features/report-hub/stability-report/event-counts-row';
import { EventSeverityFilter } from '$features/report-hub/stability-report/severity-filter';
import { type EventCounts } from '$features/report-hub/stability-report/stability-report.types';

import styles from './run-record-info-card.module.scss';

interface RunRecordInfoCardProps {
  report: RunRecordReportDetailModel;
  /**
   * Events list from the events endpoint. Used to derive the per-severity
   * badges in the header. When undefined (events not loaded yet) the row is
   * hidden — once events arrive the card re-renders with counts populated.
   */
  events?: DeviceEvent[];
  onEventTypeClick?: (severity: EventSeverityFilter) => void;
}

function countEventsBySeverity(
  events: DeviceEvent[] | undefined,
): EventCounts | undefined {
  if (!events) return undefined;
  const counts = { errors: 0, alerts: 0, warnings: 0, infos: 0 };
  events.forEach((event) => {
    const tags = (event.tags ?? []).map((t) => t.toLowerCase());
    if (tags.includes('error')) counts.errors += 1;
    else if (tags.includes('alert')) counts.alerts += 1;
    else if (tags.includes('warning') || tags.includes('warn')) counts.warnings += 1;
    else if (tags.includes('info') || tags.includes('notice')) counts.infos += 1;
  });
  return counts;
}

function StepLine({
  label,
  step,
}: {
  label: string;
  step?: Pick<RunRecordStep, 'time' | 'temperature' | 'speed' | 'speed_mode'>;
}): ReactElement {
  const time = step?.time !== undefined ? formatRunDuration(step.time) : null;
  return (
    <>
      <div className={styles.stepLabel}>{label}</div>
      <div className={styles.stepValues}>
        {time && (
          <span className={`${styles.stepValue} ${styles.growingStepValue}`}>
            <Icon name="time" size={IconSizeClasses.XSmall} />
            {time}
          </span>
        )}
        {step?.temperature !== undefined && (
          <span className={`${styles.stepValue} ${styles.growingStepValue}`}>
            <Icon name="temperature" size={IconSizeClasses.XSmall} />
            {step.temperature}°C
          </span>
        )}
        {step?.speed !== undefined && (
          <span className={`${styles.stepValue} ${styles.growingStepValue}`}>
            <Icon name="spin" size={IconSizeClasses.XSmall} />
            {step.speed}
            {step.speed_mode ?? 'rpm'}
          </span>
        )}
      </div>
    </>
  );
}

export function RunRecordInfoCard({
  report,
  events,
  onEventTypeClick,
}: RunRecordInfoCardProps): ReactElement {
  const { t } = useTranslation();
  const outcomeAppearance = getRunRecordOutcomeAppearance(report.runOutcome);
  const eventCounts = useMemo(() => countEventsBySeverity(events), [events]);

  const startedByLabel = isKnownUser(report.user)
    ? t('reportHub.table.startedBy', { user: report.user })
    : t('reportHub.table.started');
  const finishedByLabel = isKnownUser(report.endUser)
    ? t('reportHub.table.endedBy', { user: report.endUser })
    : t('reportHub.table.ended');

  const steps = Array.isArray(report.steps) ? report.steps : [];
  // The "initial parameters" line in the screenshot mirrors the first step
  // (most centrifuge runs start with the program's initial state).
  const initialStep = steps[0];

  const totalDurationSeconds = Math.floor(
    (new Date(report.endDate).getTime() - new Date(report.startDate).getTime()) / 1000,
  );
  const totalDurationLabel = formatRunDuration(totalDurationSeconds);

  return (
    <Card className="m-bottom-l">
      <Card.Header>
        <div className="flex flex__jc--center p-l w-full">
          <DeviceNameCell
            // DeviceNameCell normalizes Device | InventoryDevice | DeviceDTO,
            // so the DTO-style keys (`name`/`model`) are accepted directly.
            device={{
              name: report.deviceName ?? '',
              model: report.deviceModel ?? report.device,
              serialNumber: report.serialNumber ?? '',
              manufacturer: report.manufacturer,
            }}
          />
        </div>
      </Card.Header>

      {/* Started/Finished and event counts row — matches the layout in the screenshot. */}
      <div className={styles.timestampsRow}>
        <div className={styles.timestampBlock}>
          <Icon name="calendar" size={IconSizeClasses.XSmall} />
          <div className={styles.timestampText}>
            <span className={styles.timestampValue}>
              {getFormattedLocalizedDateWithMonthName(report.startDate)}
            </span>
            <span className={styles.timestampMeta}>{startedByLabel}</span>
          </div>
        </div>
        <span className={styles.timestampSeparator}>—</span>
        <div className={styles.timestampBlock}>
          <div className={styles.timestampText}>
            <span className={styles.timestampValue}>
              {getFormattedLocalizedDateWithMonthName(report.endDate)}
            </span>
            <span className={styles.timestampMeta}>{finishedByLabel}</span>
          </div>
        </div>
      </div>

      {events && (
        <div className="p-top-l p-bottom-xl">
          <EventCountsRow eventCounts={eventCounts} onEventTypeClick={onEventTypeClick} />
        </div>
      )}

      <hr className={styles.divider} />

      {/* Program header: program name + total duration + run outcome */}
      <div className={styles.programHeader}>
        <div className={styles.programName}>
          <Icon name="multi-step-program" size={IconSizeClasses.Small} />
          <div className="flex flex__dir--column">
            <span className="font-weight-bold">{report.programName}</span>
            {steps.length > 0 && (
              <span className="color-gray-800 font-size-s">
                {t('runRecordDetail.info.stepCount', { count: steps.length })}
              </span>
            )}
          </div>
        </div>
        <div className={styles.outcomeBlock}>
          <div className="flex flex__ai--center gap-xs">
            <Icon
              name={outcomeAppearance.icon}
              size={IconSizeClasses.XSmall}
              className={outcomeAppearance.colorClassName}
            />
            <span className="font-weight-bold">
              {t(`reportHub.table.runOutcomes.${report.runOutcome}`, {
                defaultValue: report.runOutcome,
              })}
            </span>
          </div>
          {totalDurationLabel && (
            <div className="flex flex__ai--center gap-xs color-gray-800 font-size-s">
              <Icon name="time" size={IconSizeClasses.XSmall} />
              {totalDurationLabel}
            </div>
          )}
        </div>
      </div>

      {/* Per-step parameters (initial parameters + each step). */}
      <div className={styles.stepsGrid}>
        {initialStep && (
          <StepLine
            label={t('runRecordDetail.info.initialParameters')}
            step={initialStep}
          />
        )}
        {report.timerTime !== null && report.timerTime !== undefined && (
          <>
            <div className={styles.stepLabel}>{t('runRecordDetail.info.timer')}</div>
            <div className={styles.stepValues}>
              <span className={styles.stepValue}>
                <Icon name="time" size={IconSizeClasses.XSmall} />
                {formatRunDuration(report.timerTime) ?? '0s'}
              </span>
              {report.timerMode && report.timerMode !== 'Disabled' && (
                <span className="color-gray-800 font-size-s">{report.timerMode}</span>
              )}
            </div>
          </>
        )}
        {steps.map((step, index) => (
          <StepLine
            // eslint-disable-next-line react/no-array-index-key -- order is stable within a run record
            key={`step-${index}`}
            label={t('runRecordDetail.info.stepN', { index: index + 1 })}
            step={step}
          />
        ))}

        {/* Centrifuge metadata. */}
        {report.radius !== null && (
          <>
            <div className={styles.stepLabel}>{t('runRecordDetail.info.radius')}</div>
            <div>{report.radius} cm</div>
          </>
        )}
        {report.countingMode && (
          <>
            <div className={styles.stepLabel}>
              {t('runRecordDetail.info.timeCounting')}
            </div>
            <span className={styles.stepValue}>
              <Icon name="time-counting" size={IconSizeClasses.XSmall} />
              {report.countingMode}
            </span>
          </>
        )}
        {(report.rampsAccelerate !== null || report.rampsDecelerate !== null) && (
          <>
            <div className={styles.stepLabel}>{t('runRecordDetail.info.ramps')}</div>
            <div className={styles.stepValues}>
              {report.rampsAccelerate !== null && (
                <span className={styles.stepValue}>
                  <Icon name="acceleration-ramp" size={IconSizeClasses.XSmall} />
                  {report.rampsAccelerate}
                </span>
              )}
              {report.rampsDecelerate !== null && (
                <span className={styles.stepValue}>
                  <Icon name="deceleration-ramp" size={IconSizeClasses.XSmall} />
                  {report.rampsDecelerate}
                </span>
              )}
            </div>
          </>
        )}
        {report.rotorName && (
          <>
            <div className={styles.stepLabel}>{t('runRecordDetail.info.rotor')}</div>
            <div className={styles.rotorValue}>
              <Icon name="rotor" size={IconSizeClasses.XSmall} />
              <span>
                {report.rotorName}
                {report.rotorSerialNumber ? ` (${report.rotorSerialNumber})` : ''}
              </span>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
